import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppCard } from '@/shared/components/ui/AppCard';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppInput } from '@/shared/components/ui/AppInput';
import { createOrganization } from '../../api/auth-api';
import { useAuthStore } from '@/shared/stores/auth-store';
import { setSessionToken } from '@/shared/services/session-token';
import styles from './CreateOrganizationPage.module.scss';

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!name.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const session = await createOrganization(name.trim());
      setSessionToken(session.token);
      setSession({
        user: session.user,
        permissions: session.permissions,
        orgId: session.selectedOrganizationId,
        organizations: session.organizations,
      });

      await navigate({ to: '/promote' });
    } catch (createError) {
      const message =
        createError && typeof createError === 'object' && 'message' in createError
          ? String(createError.message)
          : 'Unable to create organization.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <AppCard className={styles.card}>
        <h1 className={styles.title}>Create your organization</h1>
        <p className={styles.subtitle}>Set up your organization to start managing ad campaigns.</p>

        <AppInput
          label="Organization Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="My Events Company"
          error={error ?? undefined}
        />

        <AppButton onClick={handleCreate} loading={isLoading} disabled={!name.trim()}>
          Create Organization
        </AppButton>
      </AppCard>
    </div>
  );
}
